import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Pencil, Trash2, Save, X, Star, Leaf, Flame, Languages, Loader2 } from "lucide-react";

const emptyDish = { name: "", description: "", price: 0, category_id: "", order: 0, is_signature: false, is_vegetarian: false, is_spicy: false, allergens: "", available: true };
const emptyCat = { name: "", slug: "", order: 0, description: "" };

export default function MenuManagement() {
  const [cats, setCats] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [editDish, setEditDish] = useState(null);
  const [error, setError] = useState("");
  const [translating, setTranslating] = useState(false);
  const [translateMsg, setTranslateMsg] = useState("");

  const translate = async (mode) => {
    if (!window.confirm(
      mode === "all"
        ? "Re-traduire TOUS les plats et catégories en anglais (écrasera les traductions existantes) ?"
        : "Traduire en anglais les plats et catégories qui n'ont pas encore de version EN ?"
    )) return;
    setTranslating(true);
    setTranslateMsg("");
    try {
      const r = await api.post("/admin/menu/translate", { mode });
      setTranslateMsg(`✓ ${r.data.categories_translated} catégorie(s) + ${r.data.dishes_translated} plat(s) traduit(s)`);
      await load();
    } catch (e) {
      setTranslateMsg(`Erreur : ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`);
    } finally {
      setTranslating(false);
      setTimeout(() => setTranslateMsg(""), 6000);
    }
  };

  const load = async () => {
    const [c, d] = await Promise.all([api.get("/menu/categories"), api.get("/menu/dishes")]);
    setCats(c.data); setDishes(d.data);
    if (!activeCat && c.data.length) setActiveCat(c.data[0].id);
  };
  useEffect(() => { load(); }, []);

  const saveCat = async () => {
    setError("");
    try {
      const payload = { ...editCat, order: Number(editCat.order) || 0 };
      if (editCat.id) await api.put(`/menu/categories/${editCat.id}`, payload);
      else await api.post("/menu/categories", payload);
      setEditCat(null);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteCat = async (id) => {
    if (!window.confirm("Supprimer la catégorie et tous ses plats ?")) return;
    await api.delete(`/menu/categories/${id}`);
    await load();
  };

  const saveDish = async () => {
    setError("");
    try {
      const payload = { ...editDish, price: parseFloat(editDish.price) || 0, order: Number(editDish.order) || 0 };
      if (editDish.id) await api.put(`/menu/dishes/${editDish.id}`, payload);
      else await api.post("/menu/dishes", payload);
      setEditDish(null);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteDish = async (id) => {
    if (!window.confirm("Supprimer ce plat ?")) return;
    await api.delete(`/menu/dishes/${id}`);
    await load();
  };

  const visibleDishes = dishes.filter((d) => d.category_id === activeCat);

  return (
    <div data-testid="admin-menu-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline">Gestion</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Carte & Plats</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            data-testid="translate-menu-btn"
            onClick={() => translate("missing")}
            disabled={translating}
            className="btn-ghost"
            title="Traduire les éléments qui n'ont pas encore de version EN"
          >
            {translating ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />}
            {translating ? "Traduction…" : "Traduire en anglais"}
          </button>
          <button data-testid="add-category-btn" onClick={() => setEditCat({ ...emptyCat, order: cats.length + 1 })} className="btn-ghost">
            <Plus size={14} /> Catégorie
          </button>
          <button data-testid="add-dish-btn" onClick={() => setEditDish({ ...emptyDish, category_id: activeCat || (cats[0]?.id || "") })} className="btn-primary" disabled={!cats.length}>
            <Plus size={14} /> Nouveau plat
          </button>
        </div>
      </div>

      {translateMsg && <p data-testid="translate-msg" className="mt-4 text-sm text-[#5A7A5A]">{translateMsg}</p>}
      {error && <p className="mt-4 text-sm text-red-400">{error}</p>}

      <div className="mt-10 flex flex-wrap gap-2 border-b border-[#D9D3C5] pb-4">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center">
            <button
              data-testid={`tab-cat-${c.slug}`}
              onClick={() => setActiveCat(c.id)}
              className={`px-4 py-2 text-sm uppercase tracking-widest ${activeCat === c.id ? "text-[#B8956A] border-b-2 border-[#B8956A]" : "text-[#203240]/70 hover:text-[#203240]"}`}
            >
              {c.name} <span className="text-xs text-[#697585]">({dishes.filter(d => d.category_id === c.id).length})</span>
            </button>
            <button onClick={() => setEditCat(c)} className="text-[#697585] hover:text-[#B8956A] ml-1 p-1"><Pencil size={12} /></button>
            <button onClick={() => deleteCat(c.id)} className="text-[#697585] hover:text-red-400 p-1"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>

      <div className="mt-8 space-y-3">
        {visibleDishes.length === 0 && <p className="text-[#697585]">Aucun plat dans cette catégorie.</p>}
        {visibleDishes.map((d) => (
          <div key={d.id} data-testid={`admin-dish-row-${d.id}`} className="flex items-center justify-between gap-4 border border-[#D9D3C5] bg-[#FFFFFF] p-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display text-xl truncate">{d.name}</p>
                {d.is_signature && <Star size={14} className="text-[#B8956A] fill-[#B8956A]" />}
                {d.is_vegetarian && <Leaf size={14} className="text-emerald-600" />}
                {d.is_spicy && <Flame size={14} className="text-red-500" />}
                {!d.available && <span className="text-xs uppercase tracking-widest text-red-400">indisponible</span>}
              </div>
              {d.description && <p className="text-sm text-[#697585] mt-1 line-clamp-2">{d.description}</p>}
            </div>
            <p className="font-display text-xl text-[#B8956A]">{d.price.toFixed(2)} €</p>
            <div className="flex gap-2">
              <button data-testid={`edit-dish-${d.id}`} onClick={() => setEditDish(d)} className="p-2 border border-[#D9D3C5] hover:border-[#B8956A]"><Pencil size={14} /></button>
              <button data-testid={`delete-dish-${d.id}`} onClick={() => deleteDish(d.id)} className="p-2 border border-[#D9D3C5] hover:border-red-400 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Category dialog */}
      {editCat && (
        <Modal title={editCat.id ? "Modifier la catégorie" : "Nouvelle catégorie"} onClose={() => setEditCat(null)}>
          <div className="space-y-5">
            <input className="field" placeholder="Nom (FR)" value={editCat.name} onChange={(e) => setEditCat({ ...editCat, name: e.target.value })} data-testid="cat-input-name" />
            <input className="field" placeholder="Slug (entrees, plats, ...)" value={editCat.slug} onChange={(e) => setEditCat({ ...editCat, slug: e.target.value })} data-testid="cat-input-slug" />
            <input type="number" className="field" placeholder="Ordre" value={editCat.order} onChange={(e) => setEditCat({ ...editCat, order: e.target.value })} data-testid="cat-input-order" />
            <textarea className="field" placeholder="Description (FR)" value={editCat.description} onChange={(e) => setEditCat({ ...editCat, description: e.target.value })} rows={2} data-testid="cat-input-desc" />
            <input className="field" placeholder="Name (EN)" value={editCat.name_en || ""} onChange={(e) => setEditCat({ ...editCat, name_en: e.target.value })} data-testid="cat-input-name-en" />
            <textarea className="field" placeholder="Description (EN)" value={editCat.description_en || ""} onChange={(e) => setEditCat({ ...editCat, description_en: e.target.value })} rows={2} data-testid="cat-input-desc-en" />
          </div>
          <div className="mt-8 flex gap-3 justify-end">
            <button onClick={() => setEditCat(null)} className="btn-ghost">Annuler</button>
            <button onClick={saveCat} data-testid="cat-save" className="btn-primary"><Save size={14}/> Enregistrer</button>
          </div>
        </Modal>
      )}

      {/* Dish dialog */}
      {editDish && (
        <Modal title={editDish.id ? "Modifier le plat" : "Nouveau plat"} onClose={() => setEditDish(null)}>
          <div className="grid grid-cols-2 gap-5">
            <input className="field col-span-2" placeholder="Nom (FR)" value={editDish.name} onChange={(e) => setEditDish({ ...editDish, name: e.target.value })} data-testid="dish-input-name" />
            <textarea className="field col-span-2" placeholder="Description (FR)" value={editDish.description} onChange={(e) => setEditDish({ ...editDish, description: e.target.value })} rows={3} data-testid="dish-input-desc" />
            <input className="field col-span-2" placeholder="Name (EN)" value={editDish.name_en || ""} onChange={(e) => setEditDish({ ...editDish, name_en: e.target.value })} data-testid="dish-input-name-en" />
            <textarea className="field col-span-2" placeholder="Description (EN)" value={editDish.description_en || ""} onChange={(e) => setEditDish({ ...editDish, description_en: e.target.value })} rows={3} data-testid="dish-input-desc-en" />
            <input type="number" step="0.5" className="field" placeholder="Prix €" value={editDish.price} onChange={(e) => setEditDish({ ...editDish, price: e.target.value })} data-testid="dish-input-price" />
            <input type="number" className="field" placeholder="Ordre" value={editDish.order} onChange={(e) => setEditDish({ ...editDish, order: e.target.value })} data-testid="dish-input-order" />
            <select className="field col-span-2" value={editDish.category_id} onChange={(e) => setEditDish({ ...editDish, category_id: e.target.value })} data-testid="dish-input-category">
              {cats.map((c) => <option key={c.id} value={c.id} className="bg-[#F6F0E7]">{c.name}</option>)}
            </select>
            <input className="field col-span-2" placeholder="Allergènes" value={editDish.allergens} onChange={(e) => setEditDish({ ...editDish, allergens: e.target.value })} data-testid="dish-input-allergens" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editDish.is_signature} onChange={(e) => setEditDish({ ...editDish, is_signature: e.target.checked })} data-testid="dish-input-signature" /> Menu Midi ★</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editDish.is_vegetarian} onChange={(e) => setEditDish({ ...editDish, is_vegetarian: e.target.checked })} data-testid="dish-input-veg" /> Végétarien</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editDish.is_spicy} onChange={(e) => setEditDish({ ...editDish, is_spicy: e.target.checked })} data-testid="dish-input-spicy" /> Épicé</label>
            <label className="flex items-center gap-2 text-sm col-span-2"><input type="checkbox" checked={editDish.available} onChange={(e) => setEditDish({ ...editDish, available: e.target.checked })} data-testid="dish-input-available" /> Disponible</label>
          </div>
          <div className="mt-8 flex gap-3 justify-end">
            <button onClick={() => setEditDish(null)} className="btn-ghost">Annuler</button>
            <button onClick={saveDish} data-testid="dish-save" className="btn-primary"><Save size={14}/> Enregistrer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="modal" className="bg-[#FFFFFF] border border-[#D9D3C5] w-full max-w-xl p-8 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <h2 className="font-display text-2xl">{title}</h2>
          <button onClick={onClose} className="text-[#697585] hover:text-[#203240]"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
